import { ActionType, CompleteOrganisationStepPayload } from "../../AppDataProvider/actions";
import { AppContext, OrganisationPage } from "../../AppDataProvider/AppDataProvider";
import { ListView, OnSelectOrganisationOpts } from "./ListView/ListView";
import { marginBottom, marginLeft, marginRight, marginTop, media } from "../../common/styles";
import { OnFoundLocationOpts, Search } from "./Search/Search";
import React, { ReactElement, useContext, useState, useEffect } from "react";
import { calculateOrganisationProximity } from "./calculateOrganisationProximity/calculateOrganisationProximity";
import { Divider } from "../../common/components";
import { filterAndSortOrganisations } from "./filterAndSortOrganisations/filterAndSortOrganisations";
import { Map } from "./Mapkit/Map/Map";
import styled from "styled-components";
type Coordinates = {
  latitude: number;
  longitude: number;
};
export function OrganisationsStep(): ReactElement {
  const {
    appDataDispatch,
    appData
  } = useContext(AppContext);

  /**
   * Attempt to retrieve cached set of coordinates from session storage
   * to avoid the user retyping this query.
   */
  const cachedCoordinatesStr = window.sessionStorage.getItem(`${appData.bookingData.sessionId}:location-search-query`);
  let cachedCoordinates: {
    searchedCoordinates: Coordinates;
    searchedString: string;
  } | null = null;
  if (cachedCoordinatesStr !== null) {
    cachedCoordinates = JSON.parse(cachedCoordinatesStr) as {
      searchedCoordinates: Coordinates;
      searchedString: string;
    };
  }

  /**
   * Is used by the map to display a marker for where
   * the user's found search location is.
   *
   * Is also used as a point of comparison when calculating
   * the proximity between the users search location
   * and each organisation.
   */
  const [searchCoordinates, setSearchCoordinates] = useState<Coordinates | null>(cachedCoordinates?.searchedCoordinates ?? null);
  const {
    locationSelection,
    unitOfMeasurement
  } = appData.bookingTool;
  const selectOrganisation = (options: OnSelectOrganisationOpts) => {
    const payload: CompleteOrganisationStepPayload = {
      updatedData: {
        organisation: options.organisation
      }
    };
    appDataDispatch({
      payload,
      type: ActionType.COMPLETE_ORGANISATION_STEP
    });
  };
  const [organisationList, setOrganisationList] = useState<OrganisationPage>(filterAndSortOrganisations(appData.bookingTool.organisations, locationSelection));
  const foundLocation = (options: OnFoundLocationOpts) => {
    const searchedCoordinates = options.coordinates;
    const searchedString = options.searchStr;
    const organisationsWithLocation = {
      ...appData.bookingTool.organisations,
      edges: appData.bookingTool.organisations.edges.filter(({
        node
      }) => {
        return node.location && node.location.latitude && node.location.longitude;
      })
    };
    const organisations = filterAndSortOrganisations({
      ...organisationsWithLocation,
      edges: organisationsWithLocation.edges.map(({
        node,
        cursor
      }) => {
        const organisation = node;
        /**
         * We cast the location object to include coordinate values
         * as we know that organisations without a location are
         * filtered out prior to this code being run.
         */
        const location = organisation.location as {
          latitude: string;
          longitude: string;
        };
        const organisationCoordinates = {
          latitude: parseFloat(location.latitude),
          longitude: parseFloat(location.longitude)
        };
        const proximityToSearchedLocation = calculateOrganisationProximity(searchedCoordinates, organisationCoordinates, {
          unitOfMeasurement
        });
        return {
          cursor,
          node: {
            ...organisation,
            proximityToSearchedLocation
          }
        };
      })
    }, locationSelection);

    // Store the coordinates to persist throughout the session.
    window.sessionStorage.setItem(`${appData.bookingData.sessionId}:location-search-query`, JSON.stringify({
      searchedCoordinates,
      searchedString
    }));
    setOrganisationList(organisations);
    setSearchCoordinates(searchedCoordinates);
  };
  useEffect(() => {
    if (cachedCoordinates) {
      if (cachedCoordinates.searchedCoordinates && cachedCoordinates.searchedString) {
        foundLocation({
          coordinates: cachedCoordinates.searchedCoordinates,
          searchStr: cachedCoordinates.searchedString
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const searchAndMapEnabled = locationSelection ? locationSelection.searchAndMapEnabled : false;
  const showOrganisationList = !!searchCoordinates || !searchAndMapEnabled;
  return <Container showOrganisationList={showOrganisationList} data-sentry-element="Container" data-sentry-component="OrganisationsStep" data-sentry-source-file="OrganisationsStep.tsx">
      {searchAndMapEnabled && <Search onFoundLocation={foundLocation} {...cachedCoordinates?.searchedString && {
      sessionLocation: cachedCoordinates.searchedString
    }} />}

      <Content data-sentry-element="Content" data-sentry-source-file="OrganisationsStep.tsx">
        {searchAndMapEnabled && <VerticalDivider />}

        <ListAndMapContainer data-sentry-element="ListAndMapContainer" data-sentry-source-file="OrganisationsStep.tsx">
          {showOrganisationList && searchAndMapEnabled && <HorizontalDivider />}

          {searchCoordinates && <MapContainer>
              <Map organisations={organisationList} searchCoordinates={searchCoordinates} onSelectOrganisation={selectOrganisation} />
            </MapContainer>}
          {showOrganisationList && <ListView organisations={organisationList} onSelectOrganisation={selectOrganisation} />}
        </ListAndMapContainer>
      </Content>
    </Container>;
}
export default OrganisationsStep;
const Container = styled.div<{
  showOrganisationList?: boolean;
}>`
  display: flex;
  flex-direction: column;
  min-height: 300px;

  align-items: center;
  @media ${media.desktop} {
    flex-direction: ${({
  showOrganisationList
}) => showOrganisationList ? "row" : "column"};
    align-items: ${({
  showOrganisationList
}) => showOrganisationList ? "flex-start" : "center"};
  }
`;
const Content = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
`;
const VerticalDivider = styled(Divider).attrs({
  vertical: true
})`
  display: none;

  @media ${media.desktop} {
    display: flex;
    min-height: 100%;
    ${marginLeft.m}
    ${marginRight.m}
  }
`;
const HorizontalDivider = styled(Divider).attrs({
  vertical: false
})`
  display: flex;
  ${marginBottom.m}
  ${marginTop.m}

  @media ${media.desktop} {
    display: none;
  }
`;
const ListAndMapContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  width: 100%;
`;
const MapContainer = styled.div`
  height: 328px;
  width: 100%;
  display: flex;
  flex-direction: row;
  ${marginBottom.l}
`;